import { IkeUnit } from '@ikegps/core-shared';
import { T, Translate } from '../../i18n';
import { InputState } from './input.state';
interface InputOptions {
	onblur?: (e?: FocusEvent) => void;
	oninput?: (e?: InputEvent) => void;
}

const RE_NUMBER = /^\s*[+-]?((\.\d+)|(\d+(\.\d+)?))$/;
export class NumberInputState extends InputState<number> {
	static readonly DEFAULT_PRECISION: number = 3;
	static readonly DEFAULT_MINIMUM_INPUT_PRECISION: number = 0;

	precision: number;
	minimumInputPrecision: number;
	hideZero: boolean = false;

	onBlurCallback: (e: FocusEvent) => void = () => undefined;
	onInputCallback: (e: InputEvent) => void = () => undefined;

	static equal(value1: number, value2: number, precision: number): boolean {
		if (value1 == null || value2 == null || isNaN(value1) || isNaN(value2)) {
			return value1 === value2;
		}

		return value1.toFixed(precision) === value2.toFixed(precision);
	}

	constructor(
		label: string,
		precision: number = NumberInputState.DEFAULT_PRECISION,
		minimumInputPrecision: number = NumberInputState.DEFAULT_MINIMUM_INPUT_PRECISION,
		options: InputOptions = {}
	) {
		super(label);
		this.precision = precision;
		this.minimumInputPrecision = minimumInputPrecision;

		if (options.onblur) {
			this.onBlurCallback = options.onblur;
		}
		if (options.oninput) {
			this.onInputCallback = options.oninput;
		}
	}

	override formatInputValue(val: number) {
		if (val == null || isNaN(val)) {
			return null;
		}

		const roundedValue = IkeUnit.round(val, this.precision);
		if (this.hideZero && roundedValue === 0) {
			return null;
		}

		return String(roundedValue);
	}

	override parseInputValue(val: string) {
		const num = this.parseNumber(val);

		if (num == null) {
			this.setError(null);
			return null;
		}

		if (isNaN(num)) {
			this.setError(T.NUMBER_INVALID);
			return null;
		}

		const splitVal = val.split('.');
		// In order to ensure that splitVal.length is at least two, we had an empty string at the end
		splitVal.push('');
		if (splitVal[1]!.length < this.minimumInputPrecision) {
			this.setError(Translate(T.NUMBER_IMPRECISE, { minimumInputPrecision: this.minimumInputPrecision }));
			return null;
		}

		this.setError(null);

		if (this.hideZero && num === 0) {
			return null;
		}

		return num;
	}

	protected parseNumber(val: string): number | null {
		if (val.trim().length === 0) {
			return null;
		}

		if (val.match(RE_NUMBER) == null) {
			return NaN;
		}

		return Number(val);
	}

	override onBlur(e: FocusEvent) {
		this.onBlurCallback(e);
	}

	override onInput(e: InputEvent) {
		this.onInputCallback(e);
	}
}
